// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/admin-panels/client-telemetry-panel.tsx
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : apps/web/src/features/admin/components/client-telemetry-panel.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo aula-courses-app
//     node fleet/fleet.ts check --repo aula-courses-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { msg } from "@lingui/core/macro";
import { type Component, For, Show } from "solid-js";
import {
  AlertTriangle,
  CloudOff,
  HelpCircle,
  KeyRound,
  type LucideProps,
  RefreshCcw,
  Smartphone,
  UploadCloud,
} from "@fleet/icons";
import { Card, CardContent } from "@fleet/ui";
import type { components } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";

type ClientTelemetry = components["schemas"]["ClientTelemetry"];

/**
 * Client version census + operational beacons (docs/CLIENT_TELEMETRY.md).
 *
 * THE DESIGN RULE HERE IS ABOUT WHAT IS NOT ALLOWED: the "unknown" count is a
 * first-class figure with its own tile at the top. It is never folded into a
 * percentage, never spread across the version rows and never hidden when it is
 * zero. The failure this panel exists to make visible is not "an old client is
 * out there" — it is "the census itself stopped working and said 100% unknown
 * for six months while every test stayed green". If that happens, the very first
 * thing on screen says so.
 */
interface Tile {
  key: string;
  label: string;
  value: string;
  hint?: string;
  icon: Component<LucideProps>;
  /** `true` paints the tile as a warning. Only ever driven by the DATA. */
  alarming: boolean;
}

export function ClientTelemetryPanel(props: { telemetry: ClientTelemetry }) {
  const { t } = useI18n();

  const census = () => props.telemetry;

  const tiles = (): Tile[] => {
    const c = census();
    return [
      {
        key: "unknown",
        label: t(msg`Devices with no telemetry`),
        value: c.unknownDevices.toLocaleString(),
        hint: t(msg`Active sessions that never reported a version.`),
        icon: HelpCircle,
        alarming: c.unknownDevices > 0,
      },
      {
        key: "reporting",
        label: t(msg`Reporting devices`),
        value: c.reportingDevices.toLocaleString(),
        hint: t(msg`Of {total} active sessions.`, { total: c.activeDevices.toLocaleString() }),
        icon: Smartphone,
        alarming: false,
      },
      {
        key: "version-unknown",
        label: t(msg`Reported without a version`),
        value: c.versionUnknownDevices.toLocaleString(),
        hint: t(msg`The header arrived but carried no build version.`),
        icon: AlertTriangle,
        alarming: c.versionUnknownDevices > 0,
      },
      {
        key: "min-contract",
        label: t(msg`Lowest contract version in the field`),
        value:
          c.minContractVersion === null || c.minContractVersion === undefined
            ? t(msg`Unknown`)
            : String(c.minContractVersion),
        hint: t(msg`{count} devices report no contract version.`, {
          count: c.contractUnknownDevices.toLocaleString(),
        }),
        icon: KeyRound,
        alarming: c.minContractVersion === null || c.minContractVersion === undefined,
      },
      {
        key: "sync-stale",
        label: t(msg`Devices not synced in over a day`),
        value: c.syncStaleDevices.toLocaleString(),
        icon: CloudOff,
        alarming: c.syncStaleDevices > 0,
      },
      {
        key: "queue-backlog",
        label: t(msg`Devices with a pending write queue`),
        value: c.queueBacklogDevices.toLocaleString(),
        hint:
          c.queueDepthMax === null || c.queueDepthMax === undefined
            ? undefined
            : t(msg`Deepest queue: {depth}`, { depth: c.queueDepthMax.toLocaleString() }),
        icon: UploadCloud,
        alarming: c.queueBacklogDevices > 0,
      },
      {
        key: "auth-failures",
        label: t(msg`Devices with auth transport failures`),
        value: c.authFailureDevices.toLocaleString(),
        hint: t(msg`A 401 on a request the client believed was authenticated.`),
        icon: KeyRound,
        alarming: c.authFailureDevices > 0,
      },
      {
        key: "sw-updates",
        label: t(msg`Stuck or failed app updates`),
        value: (c.swUpdateFailedDevices + c.swUpdatePendingDevices).toLocaleString(),
        hint: t(msg`{failed} failed, {pending} installed but not applied.`, {
          failed: c.swUpdateFailedDevices.toLocaleString(),
          pending: c.swUpdatePendingDevices.toLocaleString(),
        }),
        icon: RefreshCcw,
        alarming: c.swUpdateFailedDevices > 0,
      },
    ];
  };

  return (
    <section class="space-y-4" data-testid="client-telemetry-panel">
      <div>
        <h3 class="text-xl font-semibold tracking-tight">{t(msg`Client versions in the field`)}</h3>
        <p class="text-sm text-muted-foreground">
          {t(msg`Active sessions that reported in the last {days} days.`, {
            days: census().windowDays,
          })}
        </p>
      </div>

      {/*
        The blind-spot disclaimer is part of the feature, not decoration. These
        apps are offline-first: a device can go weeks without connecting, so it
        reports nothing and silently leaves the census. Raising `min_supported`
        on "no signal = no client" would break exactly those users. Saying it in
        the UI — not only in the docs — is the point, because the person about to
        raise the number is reading this screen, not the docs.
      */}
      <p
        class="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-3 text-sm text-muted-foreground"
        data-testid="client-telemetry-blind-spot"
      >
        {t(
          msg`Absence of signal is not absence of clients. An offline-first device can go weeks without connecting and will not appear here. Treat this census as a floor, never as a complete inventory.`,
        )}
      </p>

      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <For each={tiles()}>
          {(tile) => (
            <Card
              data-testid={`client-telemetry-tile-${tile.key}`}
              classList={{ "border-warning": tile.alarming }}
            >
              <CardContent class="p-5 space-y-1">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm text-muted-foreground">{tile.label}</span>
                  <tile.icon class="h-4 w-4 shrink-0 text-primary" />
                </div>
                <div
                  class="text-3xl font-bold tracking-tight"
                  data-testid={`client-telemetry-value-${tile.key}`}
                >
                  {tile.value}
                </div>
                <Show when={tile.hint}>
                  {(hint) => <p class="text-xs text-muted-foreground">{hint()}</p>}
                </Show>
              </CardContent>
            </Card>
          )}
        </For>
      </div>

      <Show
        when={census().versions.length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="client-telemetry-empty">
            {t(msg`No device has reported its version yet.`)}
          </p>
        }
      >
        <div class="overflow-x-auto rounded-lg border">
          <table class="w-full text-sm" data-testid="client-telemetry-versions">
            <thead class="bg-muted/50 text-left">
              <tr>
                <th class="px-3 py-2 font-medium">{t(msg`Version`)}</th>
                <th class="px-3 py-2 font-medium">{t(msg`Build`)}</th>
                <th class="px-3 py-2 font-medium">{t(msg`Platform`)}</th>
                <th class="px-3 py-2 font-medium">{t(msg`Contract`)}</th>
                <th class="px-3 py-2 text-right font-medium">{t(msg`Devices`)}</th>
              </tr>
            </thead>
            <tbody>
              <For each={census().versions}>
                {(row) => (
                  <tr class="border-t" data-testid="client-telemetry-version-row">
                    {/*
                      A null field prints "Unknown" in every column. It is never
                      blank: an empty cell and a cell that was never reported look
                      identical, and that ambiguity is precisely what a census is
                      for removing.
                    */}
                    <td class="px-3 py-2 font-medium" data-testid="client-telemetry-version-value">
                      {row.appVersion ?? t(msg`Unknown`)}
                    </td>
                    <td class="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {row.buildHash ?? t(msg`Unknown`)}
                    </td>
                    <td class="px-3 py-2">{row.platform ?? t(msg`Unknown`)}</td>
                    <td class="px-3 py-2">{row.contractVersion ?? t(msg`Unknown`)}</td>
                    <td class="px-3 py-2 text-right tabular-nums">
                      {row.devices.toLocaleString()}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}
